import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";

import team1 from "../assets/va-dev.jpg";
import team2 from "../assets/Executive Partners.jpg";
import team3 from "../assets/Technical Dev.jpg";
import team4 from "../assets/va-devteam.jpg";
import team5 from "../assets/vateam.jpg";

/* ICONS */
import {
  FaGoogle,
  FaFacebook,
  FaWordpress,
  FaMicrosoft,
  FaChartLine,
} from "react-icons/fa";

import {
  SiZapier,
  SiNotion,
  SiCanva,
  SiClickup,
  SiSquarespace,
  SiMailchimp,
  SiZoho,
  SiTrello,
  SiAsana,
  SiBuffer,
  SiHootsuite,
  SiWix,
} from "react-icons/si";

/* TOOL MAP */
const toolIcons = {
  HighLevel: <FaChartLine />,
  Squarespace: <SiSquarespace />,
  Zapier: <SiZapier />,
  ClickUp: <SiClickup />,
  Notion: <SiNotion />,
  Canva: <SiCanva />,
  Meta: <FaFacebook />,
  Mailchimp: <SiMailchimp />,
  "Zoho CRM": <SiZoho />,
  Trello: <SiTrello />,
  Asana: <SiAsana />,
  Microsoft: <FaMicrosoft />,
  Buffer: <SiBuffer />,
  Hootsuite: <SiHootsuite />,
  Google: <FaGoogle />,
  WordPress: <FaWordpress />,
  Wix: <SiWix />,
};

const tools = Object.keys(toolIcons);

/* CAROUSEL IMAGES */
const images = [team1, team2, team3, team4, team5];

/* TEXT */
const titleWords = [
  "Leading",
  "Executive",
  "Virtual",
  "Assistant",
  "Solutions",
];

const phrases = [
  "Scale smarter.",
  "Operate efficiently.",
  "Lead confidently.",
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  /* ✅ ADDED */
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    date: "",
    time: "",
  });

  const [bookedSlots, setBookedSlots] = useState([]);

  /* CAROUSEL AUTO */
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  /* PHRASE LOOP */
  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  /* MODAL LOCK */
  useEffect(() => {
    document.body.style.overflow = openModal ? "hidden" : "auto";
  }, [openModal]);

  /* FETCH BOOKED SLOTS */
  useEffect(() => {
    if (!formData.date) return;

    fetch(`https://rcreate.solutions/availability?date=${formData.date}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setBookedSlots(data.bookedSlots);
        }
      })
      .catch(err => console.error(err));
  }, [formData.date]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /* ✅ UPDATED SUBMIT */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch("https://rcreate.solutions/book", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          datetime: `${formData.date}T${formData.time}:00`, // ✅ ADDED
        }),
      });

      if (!res.ok) throw new Error("Server error");

      const data = await res.json();

      if (data.success) {
        alert("Booking confirmed! Check your email.");

        /* ✅ RESET FORM */
        setFormData({
          name: "",
          email: "",
          date: "",
          time: "",
        });

        /* ✅ AUTO DOWNLOAD SUPPORT */
        if (data.downloadUrl) {
          window.location.href = data.downloadUrl;
        }

      } else {
        alert(data.message || "Time slot already booked");
      }

      setOpenModal(false);

    } catch (error) {
      console.error(error);
      alert("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center text-white px-6 py-12 overflow-hidden">

      {/* 🔥 BACKGROUND OVERLAY (3 IMAGES ROTATING) */}
      <div className="absolute inset-0 -z-10">
        {[images[0], images[1], images[2]].map((img, i) => (
          <motion.img
            key={i}
            src={img}
            className="absolute w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: current % 3 === i ? 1 : 0 }}
            transition={{ duration: 1 }}
          />
        ))}

        <div className="absolute inset-0 bg-black/70" />
      </div>

      <div className="max-w-7xl mx-auto w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center">

        {/* LEFT */}
        <div className="space-y-6 text-center md:text-left">

          <h1 className="md:hidden text-3xl font-bold">
            RCreate Virtual Assistance Services
          </h1>

          <h1 className="hidden md:flex font-heading text-[40px] md:text-[52px] leading-tight flex-wrap gap-2">
            {titleWords.map((word, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                {word}
              </motion.span>
            ))}
            <span className="text-accent">
              for Growing Businesses
            </span>
          </h1>

          <div className="md:hidden h-6">
            <AnimatePresence mode="wait">
              <motion.span
                key={phraseIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-accent font-semibold"
              >
                {phrases[phraseIndex]}
              </motion.span>
            </AnimatePresence>
          </div>

          <p className="hidden md:block text-white/80 text-lg leading-relaxed max-w-xl">
            <strong className="text-accent">
              Scale smarter. Operate efficiently. Lead confidently.
            </strong>
            <br /><br />
            Rcreate Virtual Assistance Services provides high-level executive,
            operations, and marketing support for founders and service-based businesses worldwide.
          </p>

          <button
            onClick={() => setOpenModal(true)}
            className="px-8 py-3 rounded-full bg-accent text-white font-semibold shadow-lg hover:scale-105 transition"
          >
            Book a Discovery Call
          </button>

          <div className="hidden md:grid grid-cols-2 gap-4 pt-4">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <h3 className="text-xl font-bold text-accent">
                <CountUp end={9} duration={2} />+
              </h3>
              <p className="text-xs">Years Experience</p>
            </div>

            <div className="bg-white/10 rounded-xl p-4 text-center">
              <h3 className="text-xl font-bold text-accent">
                <CountUp end={300} duration={2} />+
              </h3>
              <p className="text-xs">Businesses Served</p>
            </div>
          </div>

        </div>

        {/* RIGHT CAROUSEL */}
        <div className="relative w-full h-[260px] sm:h-[320px] md:h-[420px] overflow-hidden rounded-2xl shadow-xl">
          {images.map((img, index) => (
            <motion.img
              key={index}
              src={img}
              className="absolute w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: current === index ? 1 : 0 }}
              transition={{ duration: 0.8 }}
            />
          ))}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, i) => (
              <div
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-3 h-3 rounded-full cursor-pointer ${
                  current === i ? "bg-accent" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>

      </div>

      {/* TOOL STRIP */}
      <div className="absolute bottom-6 left-0 w-full overflow-hidden opacity-60">
        <motion.div
          className="flex gap-8 w-max"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ repeat: Infinity, duration: 60, ease: "linear" }}
        >
          {[...tools, ...tools].map((tool, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="text-accent text-lg">
                {toolIcons[tool]}
              </span>
              {tool}
            </div>
          ))}
        </motion.div>
      </div>

      {/* MODAL */}
      <AnimatePresence>
        {openModal && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-40"
              onClick={() => setOpenModal(false)}
            />

            <motion.div
              className="fixed z-50 top-1/2 left-1/2 w-[95%] sm:w-[400px] bg-white rounded-2xl p-6 -translate-x-1/2 -translate-y-1/2"
            >
              <h2 className="text-xl font-bold text-black mb-4 text-center">
                Book Your Appointment
              </h2>

              <form onSubmit={handleSubmit} className="space-y-3">

                <input name="name" required placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-3 border text-black" />

                <input name="email" type="email" required placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-3 border text-black" />

                {/* ✅ ADDED: prevent past */}
                <input type="date" name="date"
                  min={new Date().toISOString().split("T")[0]}
                  required
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full p-3 border text-black" />

                {/* ✅ 24-HOUR FORMAT */}
                <select name="time" required
                  value={formData.time}
                  onChange={handleChange}
                  className="w-full p-3 border text-black">

                  <option value="">Select Time</option>

                  {[
                    "09:00","09:30","10:00","10:30",
                    "11:00","11:30","12:00","12:30",
                    "13:00","13:30","14:00","14:30",
                    "15:00","15:30","16:00","16:30",
                    "17:00","17:30","18:00","18:30",
                    "19:00","19:30","20:00"
                  ].map((slot) => (
                    <option
                      key={slot}
                      value={slot}
                      disabled={bookedSlots.includes(slot)}
                    >
                      {slot} {bookedSlots.includes(slot) ? "(Booked)" : ""}
                    </option>
                  ))}

                </select>

                <button
                  disabled={loading}
                  className="w-full bg-accent text-white py-2 rounded-lg"
                >
                  {loading ? "Booking..." : "Confirm"}
                </button>

              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </section>
  );
}